/**
 * Feelings.
 *
 * Built against taskdojo-app/src/screens/MoodTrackerScreen.js, titled
 * "Feelings" there and here -- the nav calls it that too.
 *
 * Entries live at `users/{uid}/feelings/{moodId}` with the phone's shape:
 *
 *     { feeling, intensity, note, timestamp, date, createdAt, updatedAt }
 *
 * `feeling` is a mood *id*, not a label, which is why both platforms have to
 * agree on the option list. `date` is a local day key, and `timestamp` is a
 * full ISO instant -- logging against a past day keeps the current clock time,
 * as the phone does, so entries stay in a sensible order within that day.
 *
 * Custom moods are not stored with the entry either; they live on the user
 * profile as `customMoods` and are merged over the built-in list. An entry
 * whose mood has since been deleted therefore has no option to resolve, and is
 * drawn in a neutral grey rather than being hidden -- it still happened.
 *
 * The thirty-day grid colours a day by sentiment balance, the phone's rule:
 * more positive than negative is green, the reverse is red, a tie is amber.
 *
 * Creating a custom mood is not ported yet -- the phone writes those to the
 * profile's `customMoods`. Ones you already made there are merged in and fully
 * usable here; you just cannot add a new one from the web.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScreenHeader, SummaryBand, ActionButton, PrimaryButton, SecondaryButton,
  Field, useInputClass, raised, pressable,
} from '../dojo';
import { MOOD_OPTIONS } from '../../services/firebaseService';
import { dayKey } from '../../utils/score';
import { formatDayLong, formatTime } from '../../utils/datetime';
import { readableOn, MARK_MIN } from '../../utils/taskColor';

interface MoodEntry {
  id: string;
  feeling: string;
  intensity: number;
  note: string;
  timestamp: string;
  date: string;
}

interface MoodOption {
  id: string;
  emoji: string;
  label: string;
  color: string;
  sentiment: string;
}

/** The phone's intensity wording and faces. */
const INTENSITY_WORDS = ['A little', 'Somewhat', 'Quite', 'Very', 'Extremely'];
const POSITIVE_FACES = ['🙂', '😊', '😄', '😁', '🤩'];
const NEGATIVE_FACES = ['😕', '🙁', '😟', '😣', '😫'];

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

type Period = 'day' | 'week' | 'month';

const MoodTracker: React.FC = () => {
  const { state } = useApp();
  const { colors, isDark } = useTheme();
  /** The surface marks and chips sit on, which is what contrast is measured against. */
  const card = isDark ? '#1B2A32' : '#FFFFFF';

  const input = useInputClass();

  const uid = state.user?.uid;
  const customMoods: MoodOption[] = ((state.user as any)?.customMoods || []);
  const options = useMemo<MoodOption[]>(
    () => [...(MOOD_OPTIONS as MoodOption[]), ...customMoods],
    [customMoods],
  );

  const [moods, setMoods] = useState<MoodEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>('week');
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<MoodOption | null>(null);
  const [intensity, setIntensity] = useState(3);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<MoodEntry | null>(null);

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(collection(db, 'users', uid, 'feelings'), (snap) => {
      const out: MoodEntry[] = [];
      snap.forEach((d) => out.push({ ...(d.data() as MoodEntry), id: d.id }));
      out.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setMoods(out);
      setLoading(false);
    }, (e) => {
      setError(e?.message || "Couldn't load your entries.");
      setLoading(false);
    });
    return unsub;
  }, [uid]);

  const optionFor = (id: string) => options.find((o) => o.id === id);

  const forDate = (d: Date) => moods.filter((m) => m.date === dayKey(d));

  /** The phone's sentiment balance: green, red, or amber for a tie. */
  const dayColour = (d: Date): string | null => {
    const list = forDate(d);
    if (list.length === 0) return null;
    let pos = 0;
    let neg = 0;
    list.forEach((m) => {
      const o = optionFor(m.feeling);
      if (o?.sentiment === 'positive') pos += 1;
      else if (o?.sentiment === 'negative') neg += 1;
    });
    if (pos > neg) return '#5FA83A';
    if (neg > pos) return '#E34234';
    return '#E8B33A';
  };

  const stats = useMemo(() => {
    const start = new Date();
    if (period === 'day') start.setHours(0, 0, 0, 0);
    else if (period === 'week') start.setDate(start.getDate() - 7);
    else start.setDate(start.getDate() - 30);

    const within = moods.filter((m) => new Date(m.timestamp) >= start);
    const counts: Record<string, number> = {};
    let total = 0;
    within.forEach((m) => {
      counts[m.feeling] = (counts[m.feeling] || 0) + 1;
      total += m.intensity || 3;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => k);
    return {
      entries: within.length,
      average: within.length ? (total / within.length).toFixed(1) : '0',
      top,
    };
  }, [moods, period]);

  /** Thirty days ending today, oldest first. */
  const calendarDays = useMemo(() => {
    const out: Date[] = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 29);
    for (let i = 0; i < 30; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push(d);
    }
    return out;
  }, []);

  const openAdd = () => {
    setPicked(null); setIntensity(3); setNote(''); setAdding(true); setError('');
  };

  const save = async () => {
    if (!uid || !picked) return;
    setSaving(true); setError('');
    try {
      const now = new Date();
      // The phone's rule: logging against another day keeps the current clock
      // time, so entries stay ordered sensibly within that day.
      const when = new Date(selectedDate);
      when.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);

      const moodId = `mood_${Date.now()}`;
      await setDoc(doc(db, 'users', uid, 'feelings', moodId), {
        feeling: picked.id,
        intensity,
        note: note.trim(),
        timestamp: when.toISOString(),
        date: dayKey(when),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setAdding(false);
    } catch (err: any) {
      setError(err?.message || "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m: MoodEntry) => {
    if (!uid) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'users', uid, 'feelings', m.id));
      setConfirmDelete(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't delete that.");
    } finally {
      setSaving(false);
    }
  };

  const dayList = forDate(selectedDate);
  const faces = picked?.sentiment === 'negative' ? NEGATIVE_FACES : POSITIVE_FACES;
  const todayKey = dayKey(new Date());

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title="Feelings"
          action={
            <ActionButton label="Log how you feel" onClick={openAdd}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </ActionButton>
          }
        />

        <SummaryBand
          items={[
            { value: stats.entries, label: 'Entries' },
            { value: stats.average, label: 'Avg intensity', color: isDark ? '#FEC200' : '#C08A1E' },
            { value: dayList.length, label: 'Selected day', color: '#4A6FA5' },
          ]}
        />

        {error && (
          <div role="alert" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="px-5 space-y-5">
          {/* ------------------------------ stats ------------------------------ */}
          <div className={`rounded-[18px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-4`}>
            <div className={`flex gap-1 p-1 rounded-[14px] mb-4 ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-100'}`}>
              {(['day', 'week', 'month'] as Period[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`flex-1 py-2 rounded-[10px] text-[13.5px] font-extrabold capitalize transition-colors
                    ${period === p ? 'bg-dojo-red text-white' : colors.text}`}
                >
                  {p}
                </button>
              ))}
            </div>

            {stats.top.length > 0 ? (
              <>
                <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2 ${colors.textMuted}`}>
                  Most felt
                </p>
                <div className="flex gap-4">
                  {stats.top.map((id) => {
                    const o = optionFor(id);
                    return (
                      <div key={id} className="flex flex-col items-center">
                        <span className="text-[26px] leading-none">{o?.emoji || '🌀'}</span>
                        <span className={`text-[12px] font-semibold mt-1 ${colors.textSecondary}`}>
                          {o?.label || 'Removed'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>
                Nothing logged in this period yet.
              </p>
            )}
          </div>

          {/* ---------------------------- 30 days ---------------------------- */}
          <div className={`rounded-[18px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-4`}>
            <h2 className="text-[17px] font-extrabold tracking-[-0.3px] mb-3">Last 30 days</h2>

            <div className="grid grid-cols-7 mb-1.5">
              {WEEKDAYS.map((d, i) => (
                <span key={i} className={`text-center text-[11px] font-extrabold ${colors.textMuted}`}>{d}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: calendarDays[0].getDay() }).map((_, i) => (
                <span key={`pad-${i}`} />
              ))}
              {calendarDays.map((d) => {
                const c = dayColour(d);
                const list = forDate(d);
                const isToday = dayKey(d) === todayKey;
                const isSelected = dayKey(d) === dayKey(selectedDate);
                return (
                  <button
                    key={dayKey(d)}
                    type="button"
                    onClick={() => setSelectedDate(new Date(d))}
                    aria-label={formatDayLong(d)}
                    aria-pressed={isSelected}
                    className={`aspect-square rounded-[10px] border-2 flex flex-col items-center justify-center
                      transition-colors
                      ${isToday ? 'border-dojo-red' : isSelected ? 'border-current' : 'border-transparent'}`}
                    style={{ backgroundColor: c ? `${c}30` : undefined }}
                  >
                    <span className={`text-[11px] font-extrabold tabular-nums ${colors.textSecondary}`}>
                      {d.getDate()}
                    </span>
                    <span className="flex gap-[1px] leading-none text-[9px]">
                      {list.slice(0, 3).map((m, i) => (
                        <span key={i}>{optionFor(m.feeling)?.emoji || '·'}</span>
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className={`flex gap-4 mt-3 text-[11.5px] font-semibold ${colors.textMuted}`}>
              {[['#5FA83A', 'Positive'], ['#E8B33A', 'Mixed'], ['#E34234', 'Negative']].map(([c, l]) => (
                <span key={l} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: c }} />
                  {l}
                </span>
              ))}
            </div>
          </div>

          {/* --------------------------- chosen day --------------------------- */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-[17px] font-extrabold tracking-[-0.3px] flex-1">
                {formatDayLong(selectedDate)}
              </h2>
              <button
                type="button"
                onClick={openAdd}
                className={`h-9 px-3 rounded-[12px] ${raised} ${pressable} ${colors.cardBorder}
                  ${colors.card} ${colors.textSecondary} text-[13px] font-extrabold`}
              >
                Log one
              </button>
            </div>

            {loading ? (
              <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
            ) : dayList.length === 0 ? (
              <div className={`rounded-[15px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card}
                p-6 flex flex-col items-center text-center`}>
                <span className="text-[32px] mb-2">🌀</span>
                <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>
                  Nothing logged for this day.
                </p>
              </div>
            ) : (
              <ul className="space-y-2 list-none p-0 m-0">
                {dayList.map((m) => {
                  const o = optionFor(m.feeling);
                  // An entry whose mood was since deleted still happened.
                  const raw = o?.color || '#8E8E93';
                  // The intensity dots are marks and need 3:1; the wash behind
                  // the emoji is decoration and keeps the true hue.
                  const colour = readableOn(raw, card, MARK_MIN);
                  return (
                    <li key={m.id}>
                      <div className={`rounded-[15px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
                        p-3.5 flex items-start gap-3`}>
                        <span
                          className="w-11 h-11 rounded-[14px] flex items-center justify-center text-[22px]
                            flex-shrink-0"
                          style={{ backgroundColor: `${raw}26` }}
                        >
                          {o?.emoji || '🌀'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[15.5px] font-extrabold">{o?.label || 'Removed mood'}</span>
                            <span className={`text-[12px] font-semibold ${colors.textMuted}`}>
                              {formatTime(m.timestamp)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-1.5">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <span
                                key={i}
                                className="w-2 h-2 rounded-full"
                                style={{
                                  backgroundColor: i <= (m.intensity || 3)
                                    ? colour
                                    : (isDark ? '#334155' : '#D6D3D1'),
                                }}
                              />
                            ))}
                            <span className={`text-[12px] font-semibold ml-1.5 ${colors.textSecondary}`}>
                              {INTENSITY_WORDS[(m.intensity || 3) - 1]}
                            </span>
                          </div>
                          {m.note && (
                            <p className={`text-[13.5px] mt-1.5 break-words ${colors.textSecondary}`}>
                              {m.note}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(m)}
                          aria-label="Delete entry"
                          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                            ${colors.textMuted} hover:text-dojo-red transition-colors`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
                              strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ------------------------------ logging ------------------------------ */}
      {adding && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={() => setAdding(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-lg max-h-[92vh] overflow-y-auto ${colors.card}
              border-2 ${colors.cardBorder} rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-[26px] font-black tracking-[-0.7px] flex-1">How are you?</h2>
              <button
                type="button"
                onClick={() => setAdding(false)}
                aria-label="Close"
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.textSecondary}`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <p className={`text-[13px] font-semibold mb-5 ${colors.textSecondary}`}>
              Logging for {formatDayLong(selectedDate)}
            </p>

            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mb-5">
              {options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setPicked(o)}
                  aria-pressed={picked?.id === o.id}
                  className={`rounded-[14px] border-2 py-2.5 flex flex-col items-center gap-1 transition-colors
                    ${picked?.id === o.id
                      ? 'border-current'
                      : `${colors.cardBorder} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}`}
                  style={picked?.id === o.id
                    ? { backgroundColor: `${o.color}26`, borderColor: o.color }
                    : undefined}
                >
                  <span className="text-[22px] leading-none">{o.emoji}</span>
                  <span className={`text-[11px] font-bold text-center leading-tight ${colors.textSecondary}`}>
                    {o.label}
                  </span>
                </button>
              ))}
            </div>

            {picked && (
              <>
                <Field label="How strongly?" className="mb-4">
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setIntensity(level)}
                        aria-pressed={intensity === level}
                        className={`flex-1 rounded-[12px] border-2 py-2 text-[20px] transition-colors
                          ${intensity === level
                            ? 'border-current'
                            : `${colors.cardBorder} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}`}
                        style={intensity === level
                          ? { backgroundColor: `${picked.color}26`, borderColor: picked.color }
                          : undefined}
                      >
                        {faces[level - 1]}
                      </button>
                    ))}
                  </div>
                  <p className="text-[13px] font-extrabold mt-2"
                    style={{ color: readableOn(picked.color, card, 4.5) }}>
                    {INTENSITY_WORDS[intensity - 1]} {picked.label.toLowerCase()}
                  </p>
                </Field>

                <Field label="Anything else?" className="mb-5">
                  <textarea
                    className={`${input} min-h-[80px] resize-y`}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional"
                    maxLength={300}
                  />
                </Field>
              </>
            )}

            {error && (
              <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
                px-4 py-3 text-[13px] font-semibold text-dojo-red">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <SecondaryButton onClick={() => setAdding(false)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton onClick={save} disabled={!picked || saving} className="flex-1">
                {saving ? 'Saving…' : 'Log it'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">Delete this entry?</h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              It will be removed from your history. This can't be undone.
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirmDelete(null)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton onClick={() => remove(confirmDelete)} disabled={saving} className="flex-1">
                {saving ? 'Deleting…' : 'Delete'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoodTracker;
